import { Button, Dialog, DialogContent } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useConfirmation } from 'components/modals/ConfirmationContext';
import { NamedFeature } from 'interfaces/features';
import { InferredFeatureConfig } from 'interfaces/inferredFeatures';
import { QualityControlLabel } from 'interfaces/qualityControlLabels';
import { Study } from 'interfaces/study';
import { isEqual, map } from 'lodash';
import React, { FunctionComponent, useState } from 'react';
import { useCurrentLabId } from 'utils/useCurrentLab';
import useStudyMutations from 'utils/useStudyMutations';
import {
  studyDialogActionButtonSx,
  StudyDialogFooter,
  StudyDialogHeader,
  studyDialogPrimaryActionSx,
} from '../StudyDialogChrome';
import { areExternalStorageSettingsComplete } from './StudyExternalStorageSettings';
import StudySettings from './StudySettings';

interface StudySettingsModalProps {
  study: Study;
  onClose: (event: any) => void;
  open: boolean;
}

const StudySettingsModal: FunctionComponent<React.PropsWithChildren<StudySettingsModalProps>> = ({
  study,
  onClose,
  open,
}) => {
  const [localHighlightedFeatures, setLocalHighlightedFeatures] = useState<NamedFeature[] | undefined>();
  const [localHiddenFeatures, setLocalHiddenFeatures] = useState<NamedFeature[] | undefined>();
  const [localLabels, setLocalLabels] = useState<QualityControlLabel[]>(undefined);
  const [localInferredFeatures, setLocalInferredFeatures] = useState<InferredFeatureConfig[]>(undefined);
  const defaultSettings: Study['settings'] = {
    defaultSlideViewMode: 'side-by-side',
    ...(study?.settings || {}),
  };
  const [updatedSettings, setUpdatedSettings] = useState<Study['settings']>(defaultSettings);
  const [updatedPlatformSettings, setUpdatedPlatformSettings] = useState<Study['platformSettings']>(undefined);
  const [localUsersAcl, setLocalUsersAcl] = useState<Study['usersAcl']>(undefined);
  const [localStudyAdmins, setLocalStudyAdmins] = useState<Study['studyAdmins']>(undefined);

  const queryClient = useQueryClient();
  const { labId } = useCurrentLabId();

  const { studyMutation } = useStudyMutations(study, {
    onSuccess: () => {
      setLocalHighlightedFeatures(undefined);
      setLocalHiddenFeatures(undefined);
      setLocalLabels(undefined);
      setLocalInferredFeatures(undefined);
      setUpdatedPlatformSettings(undefined);
      setUpdatedSettings(defaultSettings);
      setLocalUsersAcl(undefined);
      setLocalStudyAdmins(undefined);
      queryClient.resetQueries({ queryKey: ['study', study.id, labId] });
      queryClient.resetQueries({ queryKey: ['procedures'], exact: false });
      queryClient.resetQueries({ queryKey: ['procedure'], exact: false });
    },
  });

  const hasDirtySettings =
    Boolean(localHighlightedFeatures) ||
    Boolean(localHiddenFeatures) ||
    Boolean(localLabels) ||
    Boolean(localInferredFeatures) ||
    Boolean(updatedPlatformSettings) ||
    Boolean(localUsersAcl) ||
    Boolean(localStudyAdmins) ||
    !isEqual(updatedSettings, defaultSettings);

  const onSave = () => {
    studyMutation.mutate({
      id: study.id,
      ...(localHighlightedFeatures && {
        highlightedFeatures: map(localHighlightedFeatures, 'key'),
      }),
      ...(localHiddenFeatures && {
        hiddenFeatures: map(localHiddenFeatures, 'key'),
      }),
      settings: { ...study.settings, ...(updatedSettings || {}) },
      ...(updatedPlatformSettings && {
        platformSettings: updatedPlatformSettings,
      }),
      ...(localLabels && {
        qcLabelsConfig: localLabels,
      }),
      ...(localInferredFeatures && {
        inferredFeaturesConfig: localInferredFeatures,
      }),
      ...(localUsersAcl && { usersAcl: localUsersAcl }),
      ...(localStudyAdmins && { studyAdmins: localStudyAdmins }),
    });
  };

  const confirmWithModal = useConfirmation();

  return (
    open && (
      <Dialog
        open={open}
        scroll="paper"
        maxWidth="lg"
        fullWidth
        onKeyDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <StudyDialogHeader title={study.name} onClose={onClose} />
        <DialogContent
          dividers
          sx={{
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            boxSizing: 'border-box',
            px: { xs: 2, sm: 3 },
            overflowX: 'hidden',
            '& > *': { width: '100%', maxWidth: '100%', minWidth: 0 },
          }}
        >
          <StudySettings
            study={study}
            setUpdatedSettings={setUpdatedSettings}
            updatedSettings={updatedSettings}
            updatedPlatformSettings={updatedPlatformSettings}
            setUpdatedPlatformSettings={setUpdatedPlatformSettings}
            localHighlightedFeatures={localHighlightedFeatures}
            setLocalHighlightedFeatures={setLocalHighlightedFeatures}
            localHiddenFeatures={localHiddenFeatures}
            setLocalHiddenFeatures={setLocalHiddenFeatures}
            localLabels={localLabels}
            setLocalLabels={setLocalLabels}
            localInferredFeatures={localInferredFeatures}
            setLocalInferredFeatures={setLocalInferredFeatures}
            localUsersAcl={localUsersAcl}
            setLocalUsersAcl={setLocalUsersAcl}
            localStudyAdmins={localStudyAdmins}
            setLocalStudyAdmins={setLocalStudyAdmins}
          />
        </DialogContent>
        <StudyDialogFooter>
          {hasDirtySettings && (
            <Button
              variant="contained"
              color="warning"
              disabled={studyMutation.isLoading}
              sx={studyDialogActionButtonSx}
              onClick={async () => {
                if (
                  await confirmWithModal({
                    title: 'Discard unsaved changes?',
                    text: 'This will discard all unsaved changes in Study Settings and restore the last saved values.',
                    confirmButtonProps: {
                      title: 'Discard changes',
                      color: 'warning',
                    },
                    cancelButtonProps: {
                      title: 'Keep Editing',
                    },
                  })
                ) {
                  setUpdatedSettings(defaultSettings);
                  setUpdatedPlatformSettings(undefined);
                  setLocalHighlightedFeatures(undefined);
                  setLocalHiddenFeatures(undefined);
                  setLocalLabels(undefined);
                  setLocalInferredFeatures(undefined);
                  setLocalUsersAcl(undefined);
                  setLocalStudyAdmins(undefined);
                }
              }}
            >
              Discard changes
            </Button>
          )}
          <Button
            variant="outlined"
            color="secondary"
            onClick={onClose}
            disabled={studyMutation.isLoading}
            sx={studyDialogActionButtonSx}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={studyMutation.isLoading || !areExternalStorageSettingsComplete(updatedSettings)}
            color="primary"
            onClick={onSave}
            sx={studyDialogPrimaryActionSx}
          >
            {studyMutation.isLoading ? 'Saving...' : 'Save'}
          </Button>
        </StudyDialogFooter>
      </Dialog>
    )
  );
};

export default StudySettingsModal;
